// CTW-DURMAH-STUDENT-MANUAL-01:
// Aggregate index for the Student Manual. This module is the ONLY thing the
// pages, the search engine and the Durmah grounding layer import.
//
// Dependency-free by design — safe in both the browser bundle and server code.

import type {
  ManualCategory,
  ManualCategoryId,
  ManualSection,
} from "./types";
import { gettingStartedSections } from "./sections/gettingStarted";
import { workspaceSections } from "./sections/workspace";
import { coreToolSections } from "./sections/coreTools";
import { planningAndTrustSections } from "./sections/planningAndTrust";

export * from "./types";

/* ------------------------------------------------------------------ */
/* Categories                                                          */
/* ------------------------------------------------------------------ */

export const MANUAL_CATEGORIES: ManualCategory[] = [
  {
    id: "getting-started",
    label: "Getting started",
    blurb: "What Durmah is, how to get in, and what to set up first.",
    icon: "Compass",
    order: 1,
  },
  {
    id: "your-workspace",
    label: "Your workspace",
    blurb: "The dashboard, the navigation, and the tools that follow you around.",
    icon: "LayoutDashboard",
    order: 2,
  },
  {
    id: "lectures",
    label: "Lectures",
    blurb: "Turning a lecture into a structured study asset.",
    icon: "FileText",
    order: 3,
  },
  {
    id: "durmah",
    label: "Talking to Durmah",
    blurb: "Text chat, voice, and what Durmah remembers.",
    icon: "Mic",
    order: 4,
  },
  {
    id: "assignments",
    label: "Assignments",
    blurb: "From the brief to the final check, without outsourcing your judgement.",
    icon: "ClipboardCheck",
    order: 5,
  },
  {
    id: "mark-my-paper",
    label: "Mark My Paper",
    blurb: "Diagnostic review of your own draft, before you submit.",
    icon: "GraduationCap",
    order: 6,
  },
  {
    id: "planning",
    label: "Planning and revision",
    blurb: "Your academic year, your modules, and how you revise.",
    icon: "CalendarDays",
    order: 7,
  },
  {
    id: "lexicon",
    label: "The Lexicon",
    blurb: "Legal vocabulary, drawn from your own lectures.",
    icon: "BookOpen",
    order: 8,
  },
  {
    id: "wellbeing",
    label: "Wellbeing",
    blurb: "Looking after yourself during the heavy weeks.",
    icon: "Heart",
    order: 9,
  },
  {
    id: "community",
    label: "Community",
    blurb: "Your university hub, the Lounge, and referrals.",
    icon: "Users",
    order: 10,
  },
  {
    id: "account",
    label: "Account and privacy",
    blurb: "Settings, billing, and what happens to your data.",
    icon: "Settings",
    order: 11,
  },
  {
    id: "integrity",
    label: "Academic integrity",
    blurb: "The boundary, stated plainly. Read this once in full.",
    icon: "ShieldCheck",
    order: 12,
  },
  {
    id: "help",
    label: "Help",
    blurb: "Common questions, and how to reach a person.",
    icon: "LifeBuoy",
    order: 13,
  },
];

/* ------------------------------------------------------------------ */
/* Sections                                                            */
/* ------------------------------------------------------------------ */

const ALL_SECTIONS: ManualSection[] = [
  ...gettingStartedSections,
  ...workspaceSections,
  ...coreToolSections,
  ...planningAndTrustSections,
];

/** Every section, sorted by category order then section order. */
export const MANUAL_SECTIONS: ManualSection[] = [...ALL_SECTIONS].sort(
  (a, b) => {
    const catA = MANUAL_CATEGORIES.find((c) => c.id === a.category)?.order ?? 99;
    const catB = MANUAL_CATEGORIES.find((c) => c.id === b.category)?.order ?? 99;
    if (catA !== catB) return catA - catB;
    return a.order - b.order;
  },
);

/* ------------------------------------------------------------------ */
/* Lookups                                                             */
/* ------------------------------------------------------------------ */

const BY_SLUG = new Map(MANUAL_SECTIONS.map((s) => [s.slug, s]));

export function getManualSection(slug: string): ManualSection | undefined {
  return BY_SLUG.get(slug);
}

export function getManualCategory(
  id: ManualCategoryId,
): ManualCategory | undefined {
  return MANUAL_CATEGORIES.find((c) => c.id === id);
}

export function getSectionsInCategory(id: ManualCategoryId): ManualSection[] {
  return MANUAL_SECTIONS.filter((s) => s.category === id);
}

export function getAllManualSlugs(): string[] {
  return MANUAL_SECTIONS.map((s) => s.slug);
}

/** Resolve `related` slugs to sections, skipping any that no longer exist. */
export function getRelatedSections(section: ManualSection): ManualSection[] {
  if (!section.related?.length) return [];
  return section.related
    .map((slug) => BY_SLUG.get(slug))
    .filter((s): s is ManualSection => Boolean(s));
}

/**
 * Neighbouring sections for previous/next navigation, in reading order.
 */
export function getAdjacentSections(slug: string): {
  previous: ManualSection | null;
  next: ManualSection | null;
} {
  const index = MANUAL_SECTIONS.findIndex((s) => s.slug === slug);
  if (index === -1) return { previous: null, next: null };
  return {
    previous: index > 0 ? MANUAL_SECTIONS[index - 1] ?? null : null,
    next:
      index < MANUAL_SECTIONS.length - 1
        ? MANUAL_SECTIONS[index + 1] ?? null
        : null,
  };
}

/** Categories that actually contain at least one section. */
export function getPopulatedCategories(): ManualCategory[] {
  return MANUAL_CATEGORIES.filter((c) =>
    MANUAL_SECTIONS.some((s) => s.category === c.id),
  ).sort((a, b) => a.order - b.order);
}

/** Total count, used in page copy and by the redaction guard. */
export const MANUAL_SECTION_COUNT = MANUAL_SECTIONS.length;
